
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Building2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import UserProfile from "@/components/UserProfile";

interface HeaderProps {
  user: any;
  organization: any;
  organizations?: any[];
  title?: string;
}

const Header = ({
  user,
  organization,
  organizations,
  title
}: HeaderProps) => {
  const navigate = useNavigate();

  // Generate a consistent color based on organization name
  const getOrganizationColor = (name: string) => {
    const colors = ['bg-[#9b87f5]',
    // Primary Purple
    'bg-[#7E69AB]',
    // Secondary Purple
    'bg-[#0EA5E9]',
    // Ocean Blue
    'bg-[#1EAEDB]',
    // Bright Blue
    'bg-[#6E59A5]' // Tertiary Purple
    ];
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  return <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 md:gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-md bg-purple-600 flex items-center justify-center">
                    <span className="font-bold text-lg text-white">E</span>
                  </div>
                  <span className="font-bold text-lg ml-2 text-purple-600 hidden sm:inline-block">Expensify</span>
                </div>
              </Link>
              {organization && <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 font-medium text-primary hover:text-primary/90">
                      {organization.logo_url ? <img src={organization.logo_url} alt={organization.name} className="w-6 h-6 rounded-full object-cover" /> : <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getOrganizationColor(organization.name)}`}>
                          <Building2 className="w-3 h-3 text-white" />
                        </div>}
                      {organization.name}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[200px]">
                    {organizations?.map(org => <DropdownMenuItem key={org.id} onClick={() => navigate(`/app/${org.id}/dashboard`)} className="cursor-pointer flex items-center gap-2">
                        {org.logo_url ? <img src={org.logo_url} alt={org.name} className="w-6 h-6 rounded-full object-cover" /> : <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getOrganizationColor(org.name)}`}>
                            <Building2 className="w-3 h-3 text-white" />
                          </div>}
                        {org.name}
                      </DropdownMenuItem>)}
                    <DropdownMenuItem onClick={() => navigate("/app/organizations")} className="cursor-pointer">
                      Manage Organizations
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>}
            </div>
          </div>
          <UserProfile user={user} />
        </div>
      </div>
    </header>;
};

export default Header;
